import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FlaskConical,
  Package,
  ArrowRight,
  Pill,
  Activity,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { FaShieldAlt, FaClock, FaAward, FaHeart } from "react-icons/fa";

export default function CategorySection() {
  const navigate = useNavigate();
  const [active, setActive] = useState(null);

  const items = [
    {
      name: "Book Lab Tests",
      subtitle: "100+ Tests • Home Sample",
      icon: <FlaskConical size={26} />,
      route: "/all-tests",
      from: "labtests",
      badge: "Fast 60m Pickup",
      color: "from-blue-600 to-indigo-600",
      bgLight: "bg-blue-50 text-blue-700",
      accentBorder: "group-hover:border-blue-300",
    },
    {
      name: "Health Packages",
      subtitle: "Full Body • Save up to 70%",
      icon: <Package size={26} />,
      route: "/all-packages",
      from: "all-packages",
      badge: "Most Popular",
      color: "from-rose-600 to-red-600",
      bgLight: "bg-rose-50 text-rose-700",
      accentBorder: "group-hover:border-rose-300",
    },
    {
      name: "Buy Medicines",
      subtitle: "100% Genuine • Fast Delivery",
      icon: <Pill size={26} />,
      route: "/medicine",
      from: "medicines",
      badge: "Flat 20% OFF",
      color: "from-emerald-600 to-teal-600",
      bgLight: "bg-emerald-50 text-emerald-700",
      accentBorder: "group-hover:border-emerald-300",
    },
    {
      name: "Diagnostic Labs",
      subtitle: "50+ NABL Verified Labs",
      icon: <Activity size={26} />,
      route: "/lab",
      from: "lab",
      badge: "NABL & ISO",
      color: "from-amber-600 to-orange-600",
      bgLight: "bg-amber-50 text-amber-700",
      accentBorder: "group-hover:border-amber-300",
    },
    {
      name: "All Checkups",
      subtitle: "Preventive Care Catalog",
      icon: <ArrowRight size={26} />,
      route: "/all",
      from: "all",
      badge: "Explore All",
      color: "from-purple-600 to-indigo-600",
      bgLight: "bg-purple-50 text-purple-700",
      accentBorder: "group-hover:border-purple-300",
    },
  ];

  const handleClick = (item, i) => {
    setActive(i);

    const payload = {
      from: item.from,
      name: item.name,
    };

    navigate(item.route, {
      state: payload,
    });
  };

  return (
    <div className="px-4 md:px-8 py-7 bg-white rounded-3xl border border-slate-200/80 shadow-xs mt-2">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 pb-4 border-b border-slate-100">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
            <span className="text-[11px] font-bold tracking-wider uppercase text-emerald-700">
              Apollo Diagnostic Services
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight">
            Our <span className="bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent">Healthcare Services</span>
          </h2>
          <p className="text-slate-500 text-xs md:text-sm mt-1">
            Book diagnostic tests, doctor consults & medicines delivered straight to your doorstep
          </p>
        </div>

        <div className="hidden md:flex items-center gap-4 text-xs font-semibold text-slate-600">
          <span className="flex items-center gap-1.5"><FaShieldAlt className="text-emerald-600" /> 100% NABL Accredited</span>
          <span className="flex items-center gap-1.5"><FaClock className="text-blue-600" /> 6-Hr Digital Reports</span>
        </div>
      </div>

      {/* Categories Grid - Apollo 5 column layout */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
        {items.map((item, i) => (
          <div
            key={i}
            onClick={() => handleClick(item, i)}
            className={`
              group relative cursor-pointer
              flex flex-col items-center justify-between
              p-4 md:p-5 rounded-2xl bg-white border border-slate-200/90 shadow-2xs
              hover:shadow-lg ${item.accentBorder}
              transition-all duration-300 transform hover:-translate-y-1
              ${active === i ? "ring-2 ring-red-500 bg-red-50/20" : ""}
            `}
          >
            {/* Top Badge */}
            {item.badge && (
              <span className={`absolute top-2.5 right-2.5 text-[9px] md:text-[10px] font-bold px-2 py-0.5 rounded-full ${item.bgLight} border border-current/10 shadow-2xs`}>
                {item.badge}
              </span>
            )}

            {/* Icon Box */}
            <div
              className={`
                w-13 h-13 md:w-16 md:h-16 mt-2
                rounded-2xl
                flex items-center justify-center
                transition-all duration-300
                shadow-sm group-hover:shadow-md
                bg-gradient-to-br ${item.color}
              `}
            >
              <div className="text-white transition-transform duration-300 group-hover:scale-110">
                {item.icon}
              </div>
            </div>

            {/* Category Name & Subtitle */}
            <div className="text-center mt-3.5 w-full">
              <span
                className={`
                  block text-xs md:text-sm font-extrabold truncate
                  transition-colors duration-200
                  ${
                    active === i
                      ? "text-red-600"
                      : "text-slate-800 group-hover:text-red-600"
                  }
                `}
              >
                {item.name}
              </span>
              <span className="block text-[11px] text-slate-400 mt-0.5 font-medium truncate">
                {item.subtitle}
              </span>
            </div>
            
            {/* Bottom Indicator Line */}
            <div className={`
              w-0 h-1 bg-gradient-to-r from-red-600 to-rose-500 mt-2.5 rounded-full
              transition-all duration-300
              ${active === i ? "w-10" : "group-hover:w-8"}
            `}></div>
          </div>
        ))}
      </div>

      {/* Trust Badges Ribbon */}
      <div className="mt-6 pt-4 border-t border-slate-100">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 bg-slate-50/80 rounded-xl p-2.5 border border-slate-200/50 hover:bg-slate-100/80 transition-all">
            <div className="w-6 h-6 bg-emerald-100 text-emerald-600 rounded-lg flex items-center justify-center shrink-0">
              <FaShieldAlt size={12} />
            </div>
            <div className="truncate">
              <span className="block font-bold">100% NABL Certified</span>
              <span className="block text-[10px] text-slate-400 font-normal">Govt Recognized Labs</span>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 bg-slate-50/80 rounded-xl p-2.5 border border-slate-200/50 hover:bg-slate-100/80 transition-all">
            <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center shrink-0">
              <FaClock size={12} />
            </div>
            <div className="truncate">
              <span className="block font-bold">Free Home Pickup</span>
              <span className="block text-[10px] text-slate-400 font-normal">Certified Phlebotomists</span>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 bg-slate-50/80 rounded-xl p-2.5 border border-slate-200/50 hover:bg-slate-100/80 transition-all">
            <div className="w-6 h-6 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center shrink-0">
              <FaAward size={12} />
            </div>
            <div className="truncate">
              <span className="block font-bold">6-Hour Reports</span>
              <span className="block text-[10px] text-slate-400 font-normal">Fast WhatsApp & PDF</span>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 bg-slate-50/80 rounded-xl p-2.5 border border-slate-200/50 hover:bg-slate-100/80 transition-all">
            <div className="w-6 h-6 bg-rose-100 text-rose-600 rounded-lg flex items-center justify-center shrink-0">
              <FaHeart size={12} />
            </div>
            <div className="truncate">
              <span className="block font-bold">10L+ Happy Patients</span>
              <span className="block text-[10px] text-slate-400 font-normal">4.8/5 Star Rating</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}